// ────────────────────────────────────────────────────────────────────────
// TrouserRestructureBlock — the shared "Pleats / Cuffs / Waistband + Belt
// colour" block used by every custom-fabric popout (CustomFabricQuestionnaire,
// CasualShirtsQuestionnaire, SweaterQuestionnaire) so the tailor sees the
// exact same restructure controls on every popout.
//
// Iter 169 (Feb 2026) — user asked for belt + pant-structure options on
// every popout and for the layout to align identically on the same
// horizontal plane. Extracted from CustomFabricQuestionnaire and shared
// so any future tweak lands in one place.
//
// Props:
//   trPleats, setTrPleats         'none' | 'single' | 'double' | null
//   trCuffs, setTrCuffs           boolean
//   trWaist, setTrWaist           null | 'belt' | 'side_tabs'
//   trBeltColor, setTrBeltColor   'Black' | 'Dark Brown' | ... | null
//   testIdPrefix                  string prefix for data-testid attrs
// ────────────────────────────────────────────────────────────────────────
import React from 'react';

const BELT_COLOURS = [
  { name: 'Black',      hex: '#0A0A0C' },
  { name: 'Dark Brown', hex: '#3A2418' },
  { name: 'Chocolate',  hex: '#503020' },
  { name: 'Cognac',     hex: '#9B5E2E' },
  { name: 'Tan',        hex: '#C19A6B' },
  { name: 'Oxblood',    hex: '#582430' },
];

export default function TrouserRestructureBlock({
  trPleats, setTrPleats,
  trCuffs, setTrCuffs,
  trWaist, setTrWaist,
  trBeltColor, setTrBeltColor,
  testIdPrefix = 'trouser',
}) {
  return (
    <div className="mt-4 space-y-3" data-testid={`${testIdPrefix}-restructure-block`}>
      {/* Pleats */}
      <div>
        <span className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1.5">Pleats</span>
        <div className="grid grid-cols-3 gap-1.5">
          {[
            { value: 'none',   label: 'No pleats',    hint: 'Flat front' },
            { value: 'single', label: 'Single pleat', hint: 'One per side' },
            { value: 'double', label: 'Double pleat', hint: 'Two per side' },
          ].map(opt => {
            const active = trPleats === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setTrPleats(active ? null : opt.value)}
                className={`p-2 border text-left transition-all ${
                  active
                    ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]'
                    : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
                }`}
                data-testid={`${testIdPrefix}-pleats-${opt.value}`}
              >
                <span className="block text-[10px] uppercase tracking-wider leading-tight">{opt.label}</span>
                <span className="block text-[9px] text-[#52525B] mt-0.5 leading-tight normal-case">{opt.hint}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Cuffs */}
      <div>
        <span className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1.5">Cuffs</span>
        <button
          type="button"
          onClick={() => setTrCuffs(v => !v)}
          className={`w-full p-2 border text-left transition-all ${
            trCuffs
              ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]'
              : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
          }`}
          data-testid={`${testIdPrefix}-cuffs`}
          aria-pressed={trCuffs}
        >
          <span className="text-[10px] uppercase tracking-wider">Cuffed hem</span>
          <span className="block text-[9px] text-[#52525B] mt-0.5 normal-case">Turned-up cuffs at the ankle</span>
        </button>
      </div>

      {/* Waistband */}
      <div>
        <span className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1.5">
          Waistband
          <span className="ml-2 normal-case text-[9px] text-[#52525B]">
            — side tabs REPLACE belt loops
          </span>
        </span>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => {
              const next = trWaist === 'belt' ? null : 'belt';
              setTrWaist(next);
              if (next !== 'belt') setTrBeltColor(null);
            }}
            className={`p-3 border-2 text-left transition-all ${
              trWaist === 'belt'
                ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/15 text-[#F5F5F5] shadow-[inset_0_0_0_1px_hsl(var(--accent))]'
                : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
            }`}
            data-testid={`${testIdPrefix}-belt`}
            aria-pressed={trWaist === 'belt'}
          >
            <span className="block text-[11px] uppercase tracking-wider font-semibold">
              Belt
            </span>
            <span className="block text-[10px] text-[#71717A] mt-1 normal-case leading-snug">
              Belt loops + leather belt at the waistband
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              const next = trWaist === 'side_tabs' ? null : 'side_tabs';
              setTrWaist(next);
              if (next === 'side_tabs') setTrBeltColor(null);
            }}
            className={`relative p-3 border-2 text-left transition-all ${
              trWaist === 'side_tabs'
                ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/15 text-[#F5F5F5] shadow-[inset_0_0_0_1px_hsl(var(--accent))]'
                : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'
            }`}
            data-testid={`${testIdPrefix}-side-tabs`}
            aria-pressed={trWaist === 'side_tabs'}
          >
            <span className="block text-[11px] uppercase tracking-wider font-semibold">
              Side tabs
              <span
                className="ml-1.5 inline-block text-[8px] uppercase tracking-[0.15em] px-1 py-0.5 border border-[hsl(var(--accent))]/50 text-[hsl(var(--accent))] align-middle"
              >
                Bespoke
              </span>
            </span>
            <span className="block text-[10px] text-[#71717A] mt-1 normal-case leading-snug">
              Adjustable side-tab buckles at each hip — NO belt loops, NO belt
            </span>
          </button>
        </div>
        {trWaist === 'belt' && (
          <div className="mt-2" data-testid={`${testIdPrefix}-belt-colour-picker`}>
            <span className="block text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1.5">
              Belt colour
              <span className="text-[9px] text-[#52525B] normal-case ml-2">{trBeltColor || '— matches shoes by default'}</span>
            </span>
            <div className="grid grid-cols-6 gap-1.5">
              {BELT_COLOURS.map(c => {
                const active = trBeltColor === c.name;
                return (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => setTrBeltColor(active ? null : c.name)}
                    title={c.name}
                    className={`aspect-square border transition-all ${active ? 'border-[hsl(var(--accent))] scale-105' : 'border-[#27272A] hover:border-[#52525B]'}`}
                    style={{ backgroundColor: c.hex }}
                    data-testid={`${testIdPrefix}-belt-colour-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                );
              })}
            </div>
            <p className="text-[9px] text-[#52525B] mt-1 normal-case">Belt & shoes render in the same leather family by default (menswear rule).</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper — build the compact `trouser_style` composite token (matches the
// backend structural_only_lock's token grammar) AND a human-readable
// description that can be appended to a fabric_note prompt.
export function buildTrouserStyleTokens({ trPleats, trCuffs, trWaist, trBeltColor }) {
  const tokens = [];
  if (trPleats === 'single')      tokens.push('single_pleat');
  else if (trPleats === 'double') tokens.push('double_pleat');
  else if (trPleats === 'none')   tokens.push('flat_front');
  if (trCuffs) tokens.push('cuffed');
  if (trWaist === 'side_tabs') {
    tokens.push('side_tabs');
  } else if (trWaist === 'belt') {
    tokens.push(trBeltColor ? `belt:${trBeltColor}` : 'belt');
  } else if (trBeltColor) {
    tokens.push(`belt:${trBeltColor}`);
  }
  return tokens.length ? tokens.join('+') : null;
}

// Helper — human sentence to APPEND onto a fabric_note prompt so Gemini
// renders the exact trouser structure the tailor picked. Emits an empty
// string when nothing was selected.
export function buildTrouserStyleDescription({ trPleats, trCuffs, trWaist, trBeltColor }) {
  const parts = [];
  if (trPleats === 'single')      parts.push('single forward pleat per side');
  else if (trPleats === 'double') parts.push('double forward pleats per side');
  else if (trPleats === 'none')   parts.push('flat front (no pleats)');
  if (trCuffs) parts.push('turned-up cuffed hem at the ankle');
  if (trWaist === 'side_tabs') {
    parts.push('side-tab adjusters at the waistband (NO belt loops, NO belt)');
  } else if (trWaist === 'belt' && trBeltColor) {
    parts.push(`belt loops with a ${trBeltColor.toLowerCase()} leather belt`);
  } else if (trWaist === 'belt') {
    parts.push('belt loops with a shoe-matched leather belt');
  } else if (trBeltColor) {
    parts.push(`${trBeltColor.toLowerCase()} leather belt`);
  }
  if (!parts.length) return '';
  return ' Trousers: ' + parts.join(', ') + '.';
}
